import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  useCoachFeedback, useCoachMessages, useCoachStarters, useCoachThreads, useCoachTurn,
  useCreateCoachThread, useDeleteCoachThread, useUpdateCoachThread,
} from '../../../api/hooks';
import type { CoachLanguage, CoachScope, CoachStarter } from '../../../api/types';
import CoachComposer from './CoachComposer';
import CoachEmptyState from './CoachEmptyState';
import CoachMessageList from './CoachMessageList';
import CoachThreadSidebar from './CoachThreadSidebar';

interface Props {
  scope: CoachScope;
  open: boolean;
  onClose: () => void;
}

const LANGS: { key: CoachLanguage; label: string }[] = [
  { key: 'en', label: 'English' }, { key: 'am', label: 'አማርኛ' }, { key: 'om', label: 'Afaan Oromoo' },
];
const LANG_KEY = 'coach_language';

function storedLanguage(): CoachLanguage {
  try {
    const v = localStorage.getItem(LANG_KEY);
    return v === 'am' || v === 'om' ? v : 'en';
  } catch { return 'en'; }
}

export default function CoachDrawer({ scope, open, onClose }: Props) {
  const threads = useCoachThreads(scope);
  const starters = useCoachStarters(scope, open);
  const [activeThreadId, setActiveThreadId] = useState<number | null>(null);
  // New chat: stay on the empty chat instead of reopening the latest thread.
  const [startingNew, setStartingNew] = useState(false);
  const [composerText, setComposerText] = useState('');
  const [asking, setAsking] = useState<string | null>(null);
  const [language, setLanguage] = useState<CoachLanguage>(storedLanguage);

  const createThread = useCreateCoachThread(scope);
  const updateThread = useUpdateCoachThread(scope);
  const deleteThread = useDeleteCoachThread(scope);
  const turn = useCoachTurn(scope);
  const messages = useCoachMessages(activeThreadId);
  const feedback = useCoachFeedback(activeThreadId);

  useEffect(() => {
    if (activeThreadId !== null || startingNew) return;
    const list = threads.data?.threads ?? [];
    if (list.length > 0) setActiveThreadId(list[0].id);
  }, [activeThreadId, startingNew, threads.data?.threads]);

  useEffect(() => {
    if (!open) turn.stop();
    return () => turn.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, turn.stop]);

  const msgList = useMemo(
    () => [...(messages.data?.pages ?? [])].reverse().flatMap((p) => p.messages),
    [messages.data?.pages]);
  const scopeLabel = starters.data?.scope.label || scope.label
    || (scope.type === 'mine' ? 'your classes' : scope.type === 'school' ? 'the school' : 'this scope');

  async function ensureThread(): Promise<number> {
    if (activeThreadId !== null) return activeThreadId;
    const created = await createThread.mutateAsync();
    setActiveThreadId(created.id);
    setStartingNew(false);
    return created.id;
  }

  async function ask(body: { content?: string; report?: 'weekly_brief' | 'meeting_summary' }) {
    turn.clearError();
    setAsking(body.content ?? (body.report === 'meeting_summary' ? 'Parent-meeting summary' : 'Weekly brief'));
    try {
      const tid = await ensureThread();
      const result = await turn.run(tid, { ...body, language });
      if (result.ok && body.content) setComposerText('');
    } catch { /* the turn hook shows the error */ } finally {
      setAsking(null);
    }
  }

  function pickStarter(s: CoachStarter) {
    if (s.kind === 'report') ask({ report: s.prompt as 'weekly_brief' | 'meeting_summary' });
    else ask({ content: s.prompt });
  }

  if (!open) return null;

  const showEmpty = !turn.pending && !asking && !turn.error && (activeThreadId === null || (messages.isSuccess && msgList.length === 0));
  const busy = turn.pending || createThread.isPending;

  return (
    <div data-testid="coach-drawer" role="dialog" aria-label={`Coach: ${scopeLabel}`}
      className="fixed inset-y-0 right-0 z-50 flex w-full border-l border-line bg-surface shadow-xl md:w-[600px]">
      <CoachThreadSidebar
        threads={threads.data?.threads ?? []}
        activeThreadId={activeThreadId}
        onSelect={(id) => { setActiveThreadId(id); setStartingNew(false); turn.clearError(); }}
        onNew={() => { setActiveThreadId(null); setStartingNew(true); turn.clearError(); }}
        onRename={(id, title) => updateThread.mutate({ threadId: id, body: { title } })}
        onArchive={(id) => { updateThread.mutate({ threadId: id, body: { archived: true } }); if (id === activeThreadId) setActiveThreadId(null); }}
        onDelete={(id) => { deleteThread.mutate(id); if (id === activeThreadId) setActiveThreadId(null); }}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-[15px] font-bold text-ink">Coach</h3>
            <p className="truncate text-xs text-muted">{scopeLabel}</p>
          </div>
          <label className="sr-only" htmlFor="coach-language">Reply language</label>
          <select id="coach-language" value={language}
            onChange={(e) => {
              const v = e.target.value as CoachLanguage;
              setLanguage(v);
              try { localStorage.setItem(LANG_KEY, v); } catch { /* private mode */ }
            }}
            className="rounded-[10px] border border-line bg-surface py-1 pl-2 pr-7 text-xs text-ink">
            {LANGS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
          </select>
          <button type="button" onClick={onClose} aria-label="Close coach"
            className="grid h-9 w-9 place-items-center rounded-[10px] text-muted hover:bg-surface-2 hover:text-ink">
            <X size={18} aria-hidden />
          </button>
        </div>
        {showEmpty
          ? <CoachEmptyState scopeLabel={scopeLabel} starters={starters.data?.starters ?? []}
              loading={starters.isLoading} onPick={pickStarter} />
          : <CoachMessageList
              messages={msgList}
              pending={turn.pending}
              streamingText={turn.text}
              toolLabel={turn.tool}
              streamError={turn.error}
              pendingQuestion={asking}
              hasOlder={!!messages.hasNextPage}
              loadingOlder={messages.isFetchingNextPage}
              onLoadOlder={() => messages.fetchNextPage()}
              onAsk={(q) => ask({ content: q })}
              onRegenerate={async (mid) => {
                if (activeThreadId === null) return;
                turn.clearError();
                await turn.run(activeThreadId, { language }, mid);
              }}
              onFeedback={(mid, value, note) => feedback.mutate({ messageId: mid, value, note })}
            />}
        <CoachComposer
          value={composerText}
          onChange={setComposerText}
          streaming={turn.pending}
          disabled={createThread.isPending}
          onSend={() => { if (!busy) ask({ content: composerText.trim() }); }}
          onStop={turn.stop}
        />
      </div>
    </div>
  );
}
